import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronSmallRightIcon } from "@wags/ui/product-icons.tsx";
import {
    DogFullBleedMediaPanel,
    DogListingPhotoWell,
    StatusSticker,
} from "@/features/listings";
import type { DogGallery } from "./dog-mobile-hero";
import { DOG_HEADER_VARS } from "./dog-desktop-layout";
import type { MoreDogsProvider } from "./more-dogs/more-dogs-breeder-card";

export function AdoptedDogSummary({
    dogId,
    name,
    breed,
    gallery,
    onOpenPhoto,
    actionRow,
    actions,
    headerHandoff,
    provider,
    recommendations,
}: {
    dogId: string;
    name: string;
    breed: string;
    gallery: DogGallery;
    onOpenPhoto: (index: number) => void;
    actionRow: ReactNode;
    actions: ReactNode;
    headerHandoff: ReactNode;
    provider: Pick<MoreDogsProvider, "id" | "name"> | null;
    recommendations: ReactNode;
}) {
    return (
        <section
            aria-label="Adopted dog summary"
            className={`page-container @container w-full px-6 pt-4 pb-10 md:pt-[calc(var(--dog-navigation-height)+1rem)] ${DOG_HEADER_VARS}`}
        >
            <div className="mx-auto max-w-4xl">
                <div className="relative z-10 grid overflow-hidden rounded-card bg-[color-mix(in_srgb,var(--havanese)_40%,var(--true-white))] md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
                    <div className="relative aspect-3/2 overflow-hidden md:aspect-auto md:min-h-72">
                        <DogListingPhotoWell state={gallery.state}>
                            <DogFullBleedMediaPanel
                                key={dogId}
                                scrollRestorationId={`adopted-gallery-${dogId}`}
                                mediaItems={gallery.mediaItems}
                                alt={name}
                                index={gallery.index}
                                onIndexChange={gallery.onIndexChange}
                                active={gallery.active}
                                mountGallery={gallery.mountGallery}
                                onOpenFullscreen={onOpenPhoto}
                                priority
                                compact
                            />
                        </DogListingPhotoWell>
                        <StatusSticker
                            id={dogId}
                            status="unavailable"
                            size="hero"
                            className="absolute top-4 left-4 z-10"
                        />
                    </div>
                    <div className="min-w-0 self-center p-4 md:p-8">
                        <div className="flex items-start justify-between gap-3">
                            <h1 className="min-w-0 flex-1 text-page-title text-balance break-words text-doberman-black">
                                {name}
                            </h1>
                            <div className="hidden shrink-0 md:flex">
                                {actions}
                            </div>
                        </div>
                        <p className="mt-2 text-body-small text-doberman-black/70">
                            {breed}
                        </p>
                        {actionRow}
                        {headerHandoff}
                    </div>
                </div>
                {provider && (
                    <Link
                        to="/provider/$providerId"
                        params={{ providerId: provider.id }}
                        className="mt-2 inline-flex min-h-11 max-w-full items-center gap-2 rounded-sm py-2 text-body-small text-doberman-black underline-offset-4 active:text-doberman-black/70 [@media(hover:hover)]:hover:underline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-doberman-black"
                    >
                        <span className="min-w-0 break-words">
                            <span className="text-doberman-black/65">
                                Listed by
                            </span>{" "}
                            <span className="font-semibold">
                                {provider.name}
                            </span>
                        </span>
                        <ChevronSmallRightIcon
                            aria-hidden="true"
                            className="size-4 shrink-0 text-doberman-black/65"
                        />
                    </Link>
                )}
            </div>
            <div className="mx-auto mt-6 max-w-4xl md:mt-8">
                {recommendations}
            </div>
        </section>
    );
}
